// Copyright ©️ 2026 eVolve MEP, LLC

import { type FocusEvent, type KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';

import { isNil, isNotNil } from 'helpers/isNotNil';

type Options = {
  value: number | null | undefined;
  /** Converts a committed value to the text shown in the field. */
  format: (value: number) => string;
  /**
   * Reads typed text as a value, already clamped and rounded to what the input allows.
   * Returns null when the text cannot be read; the field then reverts to the last committed value.
   */
  parse: (text: string) => number | null;
  onCommit: (value: number | null) => void;
  /** When true, clearing the field commits `null`. When false, a cleared field reverts. */
  allowBlank?: boolean;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  /** Called after Enter commits the typed value. */
  onSubmit?: () => void;
};

/**
 * Drives a text field that holds a number, where typing only takes effect when committed.
 *
 * - Focusing the field selects its text, so typing replaces it.
 * - Enter or leaving the field commits the typed value. Escape reverts it.
 * - Text that cannot be parsed is ignored, and the last committed value shows again. Cleared text commits `null`
 *   when `allowBlank` is set.
 *
 * `commitDraft` commits any typed text and returns the latest committed value. Use it before applying a relative
 * change (such as a stepper button) so a value typed just before the tap is not lost.
 */
export const useCommittedNumberInput = ({
  value,
  format,
  parse,
  onCommit,
  allowBlank = false,
  onFocus,
  onBlur,
  onSubmit,
}: Options) => {
  // `null` while the user is not typing; the field then shows the formatted `value`.
  const [draft, setDraft] = useState<string | null>(null);
  // Tracks the latest committed value, since the parent's `value` only updates on the next render.
  const committed = useRef(value ?? null);
  useEffect(() => {
    committed.current = value ?? null;
  }, [value]);

  const commitDraft = useCallback(() => {
    if (isNil(draft)) return committed.current;
    setDraft(null);
    if (allowBlank && draft.trim() === '') {
      committed.current = null;
      if (isNotNil(value)) onCommit(null);
      return null;
    }
    const parsed = parse(draft);
    if (isNil(parsed)) return committed.current;
    committed.current = parsed;
    if (parsed !== value) onCommit(parsed);
    return parsed;
  }, [draft, parse, value, onCommit, allowBlank]);

  const inputProps = {
    value: draft ?? (isNil(value) ? '' : format(value)),
    onChange: (event: { currentTarget: { value: string } }) => setDraft(event.currentTarget.value),
    onFocus: (event: FocusEvent<HTMLInputElement>) => {
      event.currentTarget.select();
      onFocus?.(event);
    },
    onBlur: (event: FocusEvent<HTMLInputElement>) => {
      commitDraft();
      onBlur?.(event);
    },
    onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        // Enter must not submit a surrounding form; it only commits this field.
        event.preventDefault();
        commitDraft();
        onSubmit?.();
      } else if (event.key === 'Escape') {
        event.stopPropagation();
        setDraft(null);
      }
    },
  };

  return { inputProps, commitDraft };
};
